import { Box, Typography, IconButton, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import React, { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import { CustomDialogComponent } from "../../../shared/elements/customDialog.component";
import { CustomInputComponent } from "../../../shared/elements/customInputs.component";
import FlagIcon from "@mui/icons-material/Flag";
import { HosterAdsDetailsComponent } from "./hoster-ads-details.component";
import { ProAccountItem, UpdateProAccountDto } from "../dto/index.dto";
import {
  useGetHosterById,
  useReactivateHosterHook,
  useSuspendHosterHook,
  useUpdateHosterHook,
} from "../hooks/hosters.hook";
import { useEventsStore } from "../../../shared/stores/events.store";
import {
  convertStatus,
  getStatusColor,
} from "../../../shared/elements/customTable.component";
import { DEFAULT_EVENT_IMAGE } from "../../../shared/constants/default-image";

export interface HosterDetailsProps {
  id: string;
  onBack: () => void;
}

const getItemImage = (item: ProAccountItem): string => {
  if (item.image) return item.image;
  const first = item.images?.[0];
  if (!first) return DEFAULT_EVENT_IMAGE;
  return (typeof first === "string" ? first : first.image) || DEFAULT_EVENT_IMAGE;
};

const getItemLocation = (item: ProAccountItem): string =>
  item.city || item.location || item.address || "";

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";

export const HosterDetailsComponent: React.FC<HosterDetailsProps> = ({
  id,
  onBack,
}) => {
  const { event } = useEventsStore();
  const { data, isLoading, error } = useGetHosterById(id, event);
  const updateHosterMutation = useUpdateHosterHook();
  const suspendHosterMutation = useSuspendHosterHook();
  const reactivateHosterMutation = useReactivateHosterHook();
  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [suspendDialogError, setSuspendDialogError] = useState<string | null>(
    null
  );
  const [reactivateError, setReactivateError] = useState<string | null>(null);

  const handleReactivateHoster = () => {
    if (reactivateHosterMutation.isPending) return;
    setReactivateError(null);
    reactivateHosterMutation.mutate(
      { id, event },
      {
        onError: (err: any) =>
          setReactivateError(
            err?.response?.data?.message || "Une erreur est survenue."
          ),
      }
    );
  };

  const handleConfirmSuspendHoster = () => {
    if (suspendHosterMutation.isPending) return;
    setSuspendDialogError(null);
    suspendHosterMutation.mutate(
      { id, event },
      {
        onSuccess: () => setSuspendConfirmOpen(false),
        onError: (err: any) =>
          setSuspendDialogError(
            err?.response?.data?.message ||
              "Une erreur est survenue lors de la suspension."
          ),
      }
    );
  };

  const isAccomodation = event === "accomodation";
  const showDiscipline =
    event === "event" || event === "activity" || event === "competition";

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    organisation: "",
    responsable: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    region: "",
    discipline: "",
    expiresAt: "",
    status: "active" as "active" | "inactive",
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleEditChange =
    (field: keyof typeof editForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const openEdit = () => {
    if (!data) return;
    const parts = (data.responsable || "").trim().split(" ");
    setEditForm({
      organisation: data.organisation || "",
      responsable: data.responsable || "",
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
      email: data.email || "",
      phone: data.phone || "",
      region: data.region || "",
      discipline: data.discipline || "",
      expiresAt: data.expiresAt ? data.expiresAt.slice(0, 10) : "",
      status: data.status,
    });
    setSaveError(null);
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (!data || updateHosterMutation.isPending) return;
    const payload: UpdateProAccountDto = {
      email: editForm.email,
      phone: editForm.phone,
      region: editForm.region,
      status: editForm.status,
    };
    if (editForm.expiresAt) payload.expiresAt = editForm.expiresAt;
    if (isAccomodation) {
      payload.firstName = editForm.firstName;
      payload.lastName = editForm.lastName;
    } else {
      payload.organisation = editForm.organisation;
      payload.responsable = editForm.responsable;
    }
    if (showDiscipline) payload.discipline = editForm.discipline;

    updateHosterMutation.mutate(
      { id, event, data: payload },
      {
        onSuccess: () => setEditOpen(false),
        onError: () =>
          setSaveError("Une erreur est survenue lors de l'enregistrement."),
      }
    );
  };

  const handleItemClick = (item: ProAccountItem, index: number) => {
    setSelectedItem({
      id: String(item.id ?? index),
      name: item.title || item.name || "Détails",
    });
  };

  const handleBackFromItem = () => {
    setSelectedItem(null);
  };

  // If an item is selected, show its details view
  if (selectedItem) {
    return (
      <HosterAdsDetailsComponent
        id={selectedItem.id}
        activityName={selectedItem.name}
        onBack={handleBackFromItem}
      />
    );
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !data) {
    return <div>Error loading account details</div>;
  }

  const infoRows: { label: string; value: string }[] = [
    { label: "Organisation:", value: data.organisation || "—" },
    { label: "Responsable:", value: data.responsable || "—" },
    { label: "Adresse email:", value: data.email || "—" },
    { label: "Téléphone:", value: data.phone || "—" },
    { label: "Région:", value: data.region || "—" },
    { label: "Discipline:", value: data.discipline || "—" },
    { label: "Date d'activation:", value: formatDate(data.activationDate) },
    { label: "Date d'expiration:", value: formatDate(data.expiresAt) },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon
              sx={{
                color: "#B4006E",
              }}
            />
          </IconButton>
          <Typography fontSize="1.15rem" fontWeight="bold" color="#B4006E">
            Détails
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {data.status === "active" ? (
            <CustomButtonComponent
              label="Suspendre"
              onClick={() => {
                setSuspendDialogError(null);
                setSuspendConfirmOpen(true);
              }}
              sx={{
                width: "120px",
                padding: "6px 18px",
                fontSize: "13px",
                background: "transparent",
                border: "2px solid #c0392b",
                color: "#c0392b",
              }}
            />
          ) : (
            <CustomButtonComponent
              label={
                reactivateHosterMutation.isPending
                  ? "Réactivation..."
                  : "Réactiver"
              }
              onClick={handleReactivateHoster}
              disabled={reactivateHosterMutation.isPending}
              sx={{
                width: "120px",
                padding: "6px 18px",
                fontSize: "13px",
              }}
            />
          )}
          <CustomButtonComponent
            label="Modifier"
            onClick={openEdit}
            sx={{
              width: "120px",
              padding: "6px 18px",
              fontSize: "13px",
            }}
          />
        </Box>
      </Box>
      {reactivateError && (
        <Typography fontSize="0.75rem" color="#c0392b" mb={1}>
          {reactivateError}
        </Typography>
      )}

      {/* Details Grid */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
        gap={1.5}
        mb={2.5}
        p={2}
        borderRadius={"10px"}
        sx={{
          backgroundColor: "#FFFDF7",
          "& > div": {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          },
        }}
      >
        {infoRows.map((row) => (
          <Box key={row.label}>
            <Typography
              fontWeight="600"
              fontSize="0.8rem"
              sx={{ minWidth: "130px" }}
            >
              {row.label}
            </Typography>
            <Typography fontSize="0.8rem">{row.value}</Typography>
          </Box>
        ))}
        <Box>
          <Typography
            fontWeight="600"
            fontSize="0.8rem"
            sx={{ minWidth: "130px" }}
          >
            Status:
          </Typography>
          <Typography
            fontWeight="bold"
            fontSize="0.8rem"
            sx={{ color: getStatusColor(convertStatus(data.status)) }}
          >
            {convertStatus(data.status)}
          </Typography>
        </Box>
      </Box>

      {/* Items */}
      <Box display="flex" gap={2} mt={2.5} flexWrap="wrap">
        {data.items.length === 0 && (
          <Typography fontSize="0.8rem" color="gray">
            Aucune annonce pour ce compte pro.
          </Typography>
        )}
        {data.items.map((item, index) => {
          const image = getItemImage(item);
          const title = item.title || item.name || "Sans titre";
          const location = getItemLocation(item);
          return (
            <Box
              key={item.id ?? index}
              onClick={() => handleItemClick(item, index)}
              sx={{
                width: { xs: "100%", sm: "calc(50% - 8px)", md: "220px" },
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              {/* Image */}
              <Box
                sx={{
                  height: "150px",
                  background: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3)), url(${image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundColor: "#ddd",
                }}
              />

              {/* Content */}
              <Box p={1.5}>
                <Typography fontWeight="600" fontSize="0.9rem" mb={0.5}>
                  {title}
                </Typography>

                {location && (
                  <Typography fontSize="0.8rem" color="gray" mb={1}>
                    {location}
                  </Typography>
                )}

                {item.price && (
                  <Typography fontWeight="600" fontSize="0.8rem">
                    À partir de {item.price}
                    <Typography
                      component="span"
                      fontSize="0.7rem"
                      color="gray"
                      ml={0.5}
                    >
                      /personne
                    </Typography>
                  </Typography>
                )}
              </Box>

              {/* Flag Icon */}
              <IconButton
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255,255,255,0.9)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,1)",
                  },
                }}
                size="small"
              >
                <FlagIcon sx={{ color: "#B4006E", fontSize: "1.2rem" }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {/* Edit Dialog */}
      <CustomDialogComponent
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Modifier le compte"
        onConfirm={handleSaveEdit}
        confirmLabel={
          updateHosterMutation.isPending ? "Enregistrement..." : "Enregistrer"
        }
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          {isAccomodation ? (
            <>
              <CustomInputComponent
                label="Prénom"
                value={editForm.firstName}
                onChange={handleEditChange("firstName")}
                sx={{ width: "100%" }}
              />
              <CustomInputComponent
                label="Nom"
                value={editForm.lastName}
                onChange={handleEditChange("lastName")}
                sx={{ width: "100%" }}
              />
            </>
          ) : (
            <>
              <CustomInputComponent
                label="Organisation"
                value={editForm.organisation}
                onChange={handleEditChange("organisation")}
                sx={{ width: "100%" }}
              />
              <CustomInputComponent
                label="Responsable"
                value={editForm.responsable}
                onChange={handleEditChange("responsable")}
                sx={{ width: "100%" }}
              />
            </>
          )}
          <CustomInputComponent
            label="Adresse email"
            type="email"
            value={editForm.email}
            onChange={handleEditChange("email")}
            sx={{ width: "100%" }}
          />
          <CustomInputComponent
            label="Téléphone"
            value={editForm.phone}
            onChange={handleEditChange("phone")}
            sx={{ width: "100%" }}
          />
          <CustomInputComponent
            label="Région"
            value={editForm.region}
            onChange={handleEditChange("region")}
            sx={{ width: "100%" }}
          />
          {showDiscipline && (
            <CustomInputComponent
              label="Discipline"
              value={editForm.discipline}
              onChange={handleEditChange("discipline")}
              sx={{ width: "100%" }}
            />
          )}
          <CustomInputComponent
            label="Date d'expiration"
            type="date"
            value={editForm.expiresAt}
            onChange={handleEditChange("expiresAt")}
            sx={{ width: "100%" }}
          />
          <Box>
            <Typography
              fontSize="0.75rem"
              fontWeight="600"
              mb={0.5}
              color="#555"
            >
              Statut
            </Typography>
            <Select
              value={editForm.status}
              onChange={(e: SelectChangeEvent) =>
                setEditForm((prev) => ({
                  ...prev,
                  status: e.target.value as "active" | "inactive",
                }))
              }
              size="small"
              fullWidth
              sx={{ borderRadius: "10px", fontSize: "0.8rem" }}
            >
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </Box>
          {saveError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              {saveError}
            </Typography>
          )}
        </Box>
      </CustomDialogComponent>

      {/* Suspend Confirmation Dialog */}
      <CustomDialogComponent
        open={suspendConfirmOpen}
        onClose={() => {
          setSuspendConfirmOpen(false);
          setSuspendDialogError(null);
        }}
        title="Suspendre le compte"
        onConfirm={handleConfirmSuspendHoster}
        confirmLabel={
          suspendHosterMutation.isPending ? "Suspension..." : "Suspendre"
        }
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={1.5} py={1}>
          <Typography fontSize="0.85rem">
            Suspendre ce compte professionnel ? Pour le moment, cette action
            ne modifie que son statut dans le backoffice — elle ne désactive
            pas ses annonces sur l'application ni ne bloque les nouvelles
            réservations.
          </Typography>
          {suspendDialogError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              {suspendDialogError}
            </Typography>
          )}
        </Box>
      </CustomDialogComponent>
    </Box>
  );
};
